export type EtapaAnatomia =
  | 'orientacao'
  | 'carroceriaFechamentos'
  | 'estrutura'
  | 'porta'
  | 'interior'
  | 'mecanica'
  | 'rodagem'
  | 'srs'
  | 'energiaVidros'
  | 'vidros'
  | 'blindagem'

export interface ConteudoEtapaAnatomia {
  id: EtapaAnatomia
  nome: string
  foco: string
  observar: string
  pergunta: string
  explicacao: string
  pontosChave: readonly string[]
  pecas: readonly string[]
  pecaInicial: string
  modo: 'externo' | 'estrutura' | 'srs' | 'interior' | 'mecanica' | 'energia'
}

export const ROTEIRO_ANATOMIA: readonly ConteudoEtapaAnatomia[] = [
  {
    "id": "orientacao",
    "nome": "1. Orientação",
    "foco": "Dianteira, traseira e lados definidos pelo sentido de marcha.",
    "observar": "Gire a maquete e identifique teto, assoalho, eixos e os acessos ao habitáculo.",
    "pergunta": "Como indicar o lado de uma peça quando o carro está tombado ou capotado?",
    "explicacao": "Os referenciais pertencem ao veículo: a esquerda continua sendo a esquerda mesmo quando ele está invertido. Neste perfil o condutor está à esquerda; isso não é universal.",
    "pontosChave": [
      "Reconheça o veículo e sua posição antes de nomear acessos.",
      "Diferencie orientação do veículo e posição da equipe."
    ],
    "pecas": [
      "paineisLateraisEsquerda",
      "paineisLateraisDireita",
      "capo",
      "tampaTraseira",
      "teto",
      "rodas"
    ],
    "pecaInicial": "paineisLateraisEsquerda",
    "modo": "externo"
  },
  {
    "id": "carroceriaFechamentos",
    "nome": "2. Exterior e fechamentos",
    "foco": "Chapas externas, capô, tampa, para-choques e portas.",
    "observar": "Compare o acabamento externo com os elementos resistentes escondidos por ele. Abra e afaste peças pela aba Construção.",
    "pergunta": "O para-choque aparente e sua alma metálica cumprem a mesma função?",
    "explicacao": "A cobertura externa dá forma e acabamento; por trás dela existem absorvedores, fixações e uma travessa resistente. Reconhecer as camadas evita confundir aparência com capacidade de suportar cargas.",
    "pontosChave": [
      "Observe fechaduras e dobradiças antes de escolher um acesso.",
      "Capô e tampa podem ter molas ou amortecedores a gás; sistemas ativos dependem do modelo."
    ],
    "pecas": [
      "capo",
      "tampaTraseira",
      "teto",
      "paraChoqueDianteiro",
      "almaParaChoqueDianteiro",
      "paraChoqueTraseiro",
      "farois",
      "lanternas",
      "retrovisores",
      "gradeFrontal",
      "paineisLateraisEsquerda"
    ],
    "pecaInicial": "capo",
    "modo": "externo"
  },
  {
    "id": "estrutura",
    "nome": "3. Estrutura e materiais",
    "foco": "Célula de segurança, colunas A/B/C, soleiras e caminhos de carga.",
    "observar": "Aproxime as colunas, soleiras e longarinas para observar seções ocas, reforços internos e abas de união na maquete. Compare a célula de segurança com as zonas de absorção.",
    "pergunta": "Uma coluna aparentemente fina é necessariamente fácil de cortar?",
    "explicacao": "Não. A seção pode esconder reforços, várias chapas e aços de alta resistência. A cor da maquete é didática; não determina composição, ponto seguro de corte ou ferramenta para um veículo real.",
    "pontosChave": [
      "Compare as construções abaixo e ative a pintura por aço em Camadas.",
      "Reconheça as duas faces do local de intervenção e consulte a ficha de salvamento."
    ],
    "pecas": [
      "colunaAEsquerda",
      "colunaBEsquerda",
      "colunaCEsquerda",
      "soleiraEsquerda",
      "longarinaDianteiraEsquerda",
      "reforcosTeto",
      "travessaPainel",
      "paraFogo",
      "subquadroDianteiro",
      "tunelCentral",
      "zonaAbsorcaoDianteira",
      "zonaAbsorcaoTraseira"
    ],
    "pecaInicial": "colunaBEsquerda",
    "modo": "estrutura"
  },
  {
    "id": "porta",
    "nome": "4. Por dentro da porta",
    "foco": "Chapa, forro, barra lateral, dobradiças, fechadura e mecanismo do vidro.",
    "observar": "As faces da porta são afastadas para revelar o conjunto. Selecione uma subpeça para comparar sua posição com a coluna.",
    "pergunta": "O que mantém a porta ligada ao carro além das dobradiças?",
    "explicacao": "O limitador e os chicotes também conectam a porta à carroceria; a fechadura a mantém travada ao batente. A barra lateral aumenta a resistência à intrusão, mas não define sozinha um ponto de aplicação de ferramenta.",
    "pontosChave": [
      "Diferencie fechadura, dobradiças e limitador.",
      "Localize fiação e mecanismo do vidro sob o forro."
    ],
    "pecas": [
      "portaDianteiraEsquerda",
      "portaDianteiraEsquerda.chapaExterna",
      "portaDianteiraEsquerda.forro",
      "portaDianteiraEsquerda.barraReforco",
      "portaDianteiraEsquerda.dobradicaSuperior",
      "portaDianteiraEsquerda.dobradicaInferior",
      "portaDianteiraEsquerda.fechadura",
      "portaDianteiraEsquerda.limitador",
      "portaDianteiraEsquerda.maquinaVidro",
      "portaDianteiraEsquerda.motorVidro",
      "portaDianteiraEsquerda.fiacao"
    ],
    "pecaInicial": "portaDianteiraEsquerda",
    "modo": "estrutura"
  },
  {
    "id": "interior",
    "nome": "5. Habitáculo e acessos",
    "foco": "Bancos, apoios de cabeça, painel, console, volante e pedaleira.",
    "observar": "Observe como cada elemento ocupa a possível rota de retirada. O teto fica oculto para facilitar a leitura do interior.",
    "pergunta": "Qual mudança simples pode criar espaço sem alterar a estrutura?",
    "explicacao": "Avalie regulagens do banco e encosto, apoio de cabeça e posição do volante, conforme o veículo e as condições da vítima. Mover um banco pode movimentar o ocupante ou agravar um aprisionamento: não é uma ação automática.",
    "pontosChave": [
      "Siga os trilhos até o assoalho e observe a região dos pés.",
      "Identifique comandos elétricos necessários antes do desligamento previsto no procedimento."
    ],
    "pecas": [
      "bancoMotorista",
      "bancoMotorista.assento",
      "bancoMotorista.encosto",
      "bancoMotorista.apoioCabeca",
      "trilhosBancos",
      "painel",
      "quadroInstrumentos",
      "consoleCentral",
      "seletorMarchas",
      "volante",
      "colunaDirecao",
      "pedais",
      "freioDeMao",
      "piso"
    ],
    "pecaInicial": "bancoMotorista",
    "modo": "interior"
  },
  {
    "id": "mecanica",
    "nome": "6. Motor e transmissão",
    "foco": "Bloco, cabeçote, admissão, cárter, transmissão, direção e escape.",
    "observar": "Siga o caminho do motor à transmissão e aos semi-eixos. A representação é de conjuntos, sem simulação interna do motor.",
    "pergunta": "Por que uma frente amassada pode reduzir o espaço na pedaleira?",
    "explicacao": "Conjuntos rígidos e a deformação da estrutura podem transmitir esforços à parede corta-fogo, painel e região dos pés. O comportamento depende do projeto e da colisão; não se presume que o motor sempre caia ou recue de uma maneira específica.",
    "pontosChave": [
      "Localize massas rígidas e fixações antes de discutir criação de espaço.",
      "Considere calor, fluidos e partes móveis mesmo após a parada do veículo."
    ],
    "pecas": [
      "motor",
      "motor.bloco",
      "motor.cabecote",
      "motor.coletorAdmissao",
      "motor.coxim",
      "carterMotor",
      "cambio",
      "semiEixos",
      "cardan",
      "diferencialTraseiro",
      "caixaDirecao",
      "escapamento",
      "escapamento.catalisador",
      "escapamento.silencioso"
    ],
    "pecaInicial": "motor",
    "modo": "mecanica"
  },
  {
    "id": "rodagem",
    "nome": "7. Freios e suspensão",
    "foco": "Rodas, discos, pinças, circuito hidráulico, ABS e suspensão.",
    "observar": "As rodas ficam ocultas nesta etapa para revelar os freios. As molas sustentam carga; os amortecedores controlam o movimento.",
    "pergunta": "Acionar o freio de estacionamento substitui a estabilização?",
    "explicacao": "Não. Ele atua sobre parte do sistema de rodagem e pode estar danificado, sem alimentação ou sem contato útil com o solo. A estabilização deve considerar a posição encontrada e os movimentos possíveis. ABS e ESC ajudam durante a condução; não estabilizam uma ocorrência.",
    "pontosChave": [
      "Os discos dianteiros são um exemplo construtivo; freios traseiros podem ser a disco ou tambor.",
      "Molas comprimidas armazenam energia. Não confunda desmontagem didática com procedimento operacional."
    ],
    "pecas": [
      "freiosDianteiros",
      "pincasFreio",
      "servoFreio",
      "cilindroMestre",
      "moduloAbs",
      "linhasFreio",
      "suspensao",
      "cubosRoda",
      "rodas",
      "freioDeMao"
    ],
    "pecaInicial": "freiosDianteiros",
    "modo": "mecanica"
  },
  {
    "id": "srs",
    "nome": "8. Segurança passiva e SRS",
    "foco": "Cintos, pretensionadores, airbags, sensores e retenção infantil.",
    "observar": "Compare os dispositivos presentes neste perfil. A posição ilustrada não substitui a ficha de salvamento.",
    "pergunta": "Um airbag acionado prova que todos os outros dispositivos estão inativos?",
    "explicacao": "Não. Outros dispositivos podem não ter acionado. A inspeção e o procedimento de desativação são específicos do fabricante; respeite o tempo e as zonas indicados no guia. Não coloque ferramentas ou o corpo no caminho de expansão de um airbag não acionado.",
    "pontosChave": [
      "Segurança passiva reduz consequências do impacto; não elimina a possibilidade de lesão.",
      "O airbag complementa o cinto. Pretensionador recolhe folga; limitador de carga controla esforço."
    ],
    "pecas": [
      "centralSrs",
      "sensorImpactoDianteiroEsquerda",
      "sensorImpactoLateralEsquerda",
      "airbagMotorista",
      "airbagPassageiro",
      "airbagLateralEsquerdo",
      "airbagCortinaEsquerdo",
      "airbagBancoMotorista",
      "airbagColunaMotorista",
      "cintoMotorista",
      "pretensionadorMotorista",
      "retratorMotorista",
      "pretensionadorFechoMotorista",
      "cintoTraseiroCentral",
      "pretensionadorTraseiroEsquerdo",
      "isofix",
      "bancoMotorista.apoioCabeca"
    ],
    "pecaInicial": "centralSrs",
    "modo": "srs"
  },
  {
    "id": "energiaVidros",
    "nome": "9. Energia e fluidos",
    "foco": "Baixa tensão, combustível, GNV, alta tensão e arrefecimento.",
    "observar": "Compare o hatch com GNV e o sedan híbrido. Só são listadas as fontes presentes no perfil selecionado.",
    "pergunta": "Desconectar a bateria de baixa tensão torna todo o veículo livre de energia?",
    "explicacao": "Não. Podem permanecer energia na bateria de tração, carga residual de sistemas, combustível, gás sob pressão, calor e energia mecânica. O desligamento deve seguir o guia do modelo; a maquete não define uma sequência universal.",
    "pontosChave": [
      "Cabos laranja indicam circuitos de alta tensão: não cortar, perfurar ou comprimir.",
      "Não perfure recipientes, não abra sistemas quentes e não use a cor de um líquido para concluir sua origem."
    ],
    "pecas": [
      "bateria12v",
      "tanqueCombustivel",
      "linhaCombustivel",
      "cilindroGnv",
      "tubulacaoGnv",
      "bateriaAltaTensao",
      "cabeamentoAltaTensao",
      "radiador",
      "condensador",
      "ventoinha",
      "reservatorioExpansao"
    ],
    "pecaInicial": "bateria12v",
    "modo": "energia"
  },
  {
    "id": "vidros",
    "nome": "10. Vidros e identificação",
    "foco": "Para-brisa, vidros laterais e vidro traseiro.",
    "observar": "Leia as marcações do vidro real: sua posição no carro não garante o tipo de construção.",
    "pergunta": "Todo vidro lateral se fragmenta como um temperado?",
    "explicacao": "Não. Laterais também podem ser laminadas ou ter outras construções. O temperado tende a se fragmentar; o laminado mantém fragmentos aderidos à camada intermediária. Ambos exigem proteção contra fragmentos e resíduos no manejo.",
    "pontosChave": [
      "Compare fragmentação e retenção dos fragmentos, sem presumir a técnica pela posição.",
      "Planeje proteção da vítima e da equipe antes do manejo."
    ],
    "pecas": [
      "paraBrisa",
      "portaDianteiraEsquerda.vidro",
      "portaTraseiraEsquerda.vidro",
      "vidroTraseiro"
    ],
    "pecaInicial": "paraBrisa",
    "modo": "externo"
  },
  {
    "id": "blindagem",
    "nome": "11. Blindagem e revisão",
    "foco": "Construções especiais e limites da identificação visual.",
    "observar": "A maquete não representa um carro blindado. Discuta espessura, sobreposição de materiais, massa e acesso com base no veículo real.",
    "pergunta": "Maior força de ferramenta resolve, por si só, uma construção blindada?",
    "explicacao": "Não. Vidros multicamadas, reforços e sobreposições podem exigir outra técnica ou outro acesso. A escolha depende da identificação, do guia aplicável, da capacidade do equipamento e da situação da vítima. Encerre explicando uma rota alternativa e seus riscos.",
    "pontosChave": [
      "Identifique o que você sabe e o que precisa confirmar.",
      "Revisão: nomeie uma estrutura, um obstáculo interno, uma fonte de energia e um dispositivo de segurança."
    ],
    "pecas": [
      "portaDianteiraEsquerda",
      "portaDianteiraEsquerda.vidro",
      "colunaBEsquerda",
      "soleiraEsquerda"
    ],
    "pecaInicial": "portaDianteiraEsquerda.vidro",
    "modo": "externo"
  }
]

export function etapaAnatomiaPorId(id: EtapaAnatomia): ConteudoEtapaAnatomia {
  return ROTEIRO_ANATOMIA.find((etapa) => etapa.id === id) ?? ROTEIRO_ANATOMIA[0]!
}

